import React, { useEffect, useState } from 'react';
import {
  Box, Container, Grid, Typography, Button, IconButton, Paper, Divider, TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';
import toast from 'react-hot-toast';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getCartTotal } = useCart();
  const { isAuthenticated, openLoginModal, user } = useAuth();

  // --- States ---
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(localStorage.getItem("appliedCouponCode") || ""); 
  const [discount, setDiscount] = useState(Number(localStorage.getItem("couponDiscount")) || 0);
  const [couponPercentDisplay, setCouponPercentDisplay] = useState(Number(localStorage.getItem("couponPercent")) || 0);
  const [couponApplied, setCouponApplied] = useState(localStorage.getItem("couponApplied") === "true");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // --- Calculations ---
  const subtotal = getCartTotal();
  const deliveryCharge = (subtotal >= 499 || subtotal === 0) ? 0 : 40;
  const discountAmount = Math.round(subtotal * discount);
  const finalPrice = Math.max((subtotal + deliveryCharge) - discountAmount, 0);

  // --- Handlers ---
  const handleRemoveCoupon = () => {
    setCoupon("");
    setAppliedCoupon("");
    setDiscount(0);
    setCouponPercentDisplay(0);
    setCouponApplied(false);
    localStorage.removeItem("couponApplied");
    localStorage.removeItem("couponDiscount");
    localStorage.removeItem("couponPercent");
    localStorage.removeItem("appliedCouponCode");
  };

  const applyCoupon = async () => {
    if (couponApplied) return toast.error("Coupon already applied!");
    if (!isAuthenticated) {
      toast.error("Please login to apply coupon");
      openLoginModal();
      return;
    }
    if (!coupon.trim()) return toast.error("Please enter a coupon code");

    try {
      const res = await axios.post("http://localhost:3001/api/offers/validate", {
        code: coupon.trim().toUpperCase(),
        subtotal: subtotal,
        userId: user?._id
      });

      if (res.data.success) {
        const decimalDiscount = res.data.discountPercent / 100;
        const couponCode = coupon.trim().toUpperCase();

        setDiscount(decimalDiscount);
        setAppliedCoupon(couponCode);
        setCouponPercentDisplay(res.data.discountPercent);
        setCouponApplied(true);

        localStorage.setItem("couponApplied", "true");
        localStorage.setItem("appliedCouponCode", couponCode);
        localStorage.setItem("couponDiscount", decimalDiscount.toString());
        localStorage.setItem("couponPercent", res.data.discountPercent.toString());

        toast.success(`${res.data.discountPercent}% discount applied!`);
      }
    } catch (err) {
      setDiscount(0);
      toast.error(err.response?.data?.message || "Invalid Coupon Code");
    }
  };

  // 🔥 FIXED FLOW: Ab ye direct order place nahi karega, Payment page bhejega
  const handleCheckout = () => {
    if (!cartItems.length) return toast.error("Your cart is empty!");
    if (!isAuthenticated) return openLoginModal();

    // Payment page ko data pass kar rahe hain
    navigate('/payment', { 
      state: { 
        items: cartItems,
        totalAmount: finalPrice,
        couponCode: appliedCoupon || null,
        discountAmount: discountAmount,
        userId: user._id
      } 
    });
  };

  // --- UI Component ---
  const CartItem = ({ item }) => (
    <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #eee' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={3} sm={2}>
          <Box onClick={() => navigate(`/viewproduct/${item._id}`)} sx={{ cursor: 'pointer', borderRadius: 2, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
            <img src={item.ProductMainImgUrl || "/placeholder.png"} alt={item.ProductName} style={{ width: '100%', height: 80, objectFit: 'cover' }} />
          </Box>
        </Grid>
        <Grid item xs={9} sm={4}>
          <Typography variant="subtitle1" fontWeight="bold">{item.ProductName}</Typography>
          <Typography variant="body2" color="text.secondary">Weight: {item.ProductSize || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => updateQuantity(item._id, Math.max(1, item.ProductQuantity - 1))} sx={{ border: '1px solid #ddd' }}><RemoveIcon fontSize="small" /></IconButton>
            <Typography sx={{ px: 2 }}>{item.ProductQuantity}</Typography>
            <IconButton size="small" onClick={() => updateQuantity(item._id, item.ProductQuantity + 1)} sx={{ border: '1px solid #ddd' }}><AddIcon fontSize="small" /></IconButton>
          </Box>
        </Grid>
        <Grid item xs={4} sm={2}>
          <Typography variant="h6" fontWeight="bold" color="#2e7d32">Rs.{formatPrice(item.ProductPrice * item.ProductQuantity)}</Typography>
        </Grid>
        <Grid item xs={2} sm={1} sx={{ textAlign: 'right' }}>
          <IconButton onClick={() => removeFromCart(item._id)} sx={{ color: '#e53935' }}><DeleteOutlineIcon /></IconButton>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb items={[{ label: 'Shopping Cart' }]} />
        <Typography variant="h4" fontWeight="bold" color="#3E2723" sx={{ mb: 3 }}>Shopping Cart</Typography>

        {cartItems.length > 0 ? (
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                {cartItems.map((item) => <CartItem key={item._id} item={item} />)}
                <Button onClick={() => navigate('/filter/All')} sx={{ mt: 2, color: '#876513' }}>Continue Shopping</Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Order Summary</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography fontWeight="bold">Rs.{formatPrice(subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography color="text.secondary">Delivery</Typography>
                  <Typography fontWeight="bold" color={deliveryCharge === 0 ? '#2e7d32' : 'inherit'}>
                    {deliveryCharge === 0 ? 'FREE' : `Rs.${deliveryCharge}`}
                  </Typography>
                </Box>
                {couponApplied && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography color="success.main">Discount ({couponPercentDisplay}%)</Typography>
                    <Typography fontWeight="bold" color="#2e7d32">-Rs.{formatPrice(discountAmount)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">Total</Typography>
                  <Typography variant="h6" fontWeight="bold" color="#2e7d32">Rs.{formatPrice(finalPrice)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <TextField
                    placeholder="Enter coupon code"
                    size="small"
                    fullWidth
                    value={coupon}
                    disabled={couponApplied}
                    onChange={(e) => setCoupon(e.target.value)}
                  />
                  {couponApplied ? (
                    <Button onClick={handleRemoveCoupon} variant="contained" color="error">REMOVE</Button>
                  ) : (
                    <Button onClick={applyCoupon} variant="outlined" sx={{ borderColor: '#876513', color: '#876513' }}>APPLY</Button>
                  )}
                </Box>
                <Button variant="contained" fullWidth size="large" onClick={handleCheckout} sx={{ py: 1.5, bgcolor: '#876513', fontWeight: 'bold' }}>
                  Proceed to Checkout
                </Button>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <ShoppingBagIcon sx={{ fontSize: 80, color: '#ddd', mb: 2 }} />
            <Typography variant="h5">Your cart is empty</Typography>
          </Paper>
        )}
      </Container>
      <Footer />
    </Box>
  );
};

export default Cart;